// Third-Party
import { Box, Palette, Stack, Typography } from "@mui/material";
import { Check, RefreshCw, Unplug, X } from "lucide-react";

// Internal
import Label from "@components/Label";
import { borderGeneralStyle, getSectionLabelStyle } from "@utils/cssStyles";
import OdooLogo from "src/assets/logo/Odoo.png";
import { IOdooConnection } from "@pages/askDost/types";
import { getIconBadgeStyle, getPillIconButtonStyle, getPillTextButtonStyle } from "@pages/askDost/styles/askDostStyles";


interface IAskDostOdooConnectorRowProps {
  palette: Palette;
  translate: (text: string, options?: any) => string;
  odoo: IOdooConnection | null;
  isSyncing: boolean;
  onConnect: () => void;
  onSync: () => void;
  onDisconnect: () => void;
}

export const AskDostOdooConnectorRow: React.FC<
  IAskDostOdooConnectorRowProps
> = ({
  palette,
  translate,
  odoo,
  isSyncing,
  onConnect,
  onSync,
  onDisconnect,
}) => {
  return (
    <Box sx={{ ...borderGeneralStyle, borderRadius: 1.25 }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ p: 1.25 }}
      >
        <Box sx={getIconBadgeStyle(palette, { variant: "bordered" })}>
          <img
            src={OdooLogo}
            alt="Odoo"
            width={26}
            style={{ display: "block" }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: palette.text.heading,
                fontSize: "13px",
              }}
            >
              Odoo
            </Typography>
            <Box
              title={translate(odoo ? "connected" : "not_connected")}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: odoo ? "success.lighter" : "error.lighter",
              }}
            >
              {odoo ? (
                <Check
                  size={10}
                  color={palette.success.dark}
                  strokeWidth={3.2}
                />
              ) : (
                <X size={9} color={palette.error.main} strokeWidth={3.4} />
              )}
            </Box>
          </Stack>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: "block",
              mt: 0.25,
              color: palette.text.label,
              fontSize: "11.5px",
              fontWeight: 400,
            }}
          >
            {odoo
              ? isSyncing
                ? translate("syncing")
                : translate("last_synced", { when: odoo.syncedAt })
              : translate("connect_your_odoo_app")}
          </Typography>
        </Box>

        {!odoo ? (
          <Box
            onClick={onConnect}
            sx={{ flexShrink: 0, ...getPillTextButtonStyle(palette) }}
          >
            {translate("connect")}
          </Box>
        ) : (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <Box
              onClick={onSync}
              title={translate("sync_now")}
              sx={getPillIconButtonStyle(palette, "primary")}
            >
              <RefreshCw size={14} />
            </Box>
            <Box
              onClick={onDisconnect}
              title={translate("disconnect")}
              sx={getPillIconButtonStyle(palette, "neutral")}
            >
              <Unplug size={14} />
            </Box>
          </Stack>
        )}
      </Stack>

      {odoo && !!odoo.syncedEntities?.length && (
        <Box
          sx={{
            px: 1.25,
            pb: 1.25,
            pt: 0.25,
            borderTop: "1px solid",
            borderColor: "text.borderLine",
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              ...getSectionLabelStyle(palette.text.label),
              fontWeight: 600,
              pt: 1,
              pb: 0.75,
              textTransform: "none",
            }}
          >
            {translate("data_synced")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {odoo.syncedEntities.map((entity) => (
              <Label key={entity.key} variant="outlined" color="info">
                <span style={{ fontWeight: 500 }}>
                  {entity.label}
                </span>
              </Label>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};